import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { strategicImperativeFragment } from 'basics/graphql/fragments/strategicImperative.fragments';
import { MutationReturnType } from 'basics/types/maya.types';
import { StrategicImperative } from 'generated/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const updateStrategicImperativeMutationCall = gql`
mutation updateStrategicImperative($id: String!, $updateStrategicImperative: UpdateStrategicImperativeInput!) {
  updateStrategicImperative(id: $id, updateStrategicImperativeInput: $updateStrategicImperative) {
    ${strategicImperativeFragment}
  }
}
`;

type ResultType = {
  updateStrategicImperative: StrategicImperative;
};

type UseUdpdateStrategicImperativeMutation = [
  (id: string | null, strategicImperative: Partial<StrategicImperative>) => Promise<MutationReturnType<StrategicImperative>>,
  MutationResult<ResultType>,
];

export const useUpdateStrategicImperativeMutation = (): UseUdpdateStrategicImperativeMutation => {
  const [updateStrategicImperative, state] = useMutation<ResultType>(updateStrategicImperativeMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });

  const executeMutation = useCallback(async (id, strategicImperative) => {
    try {
      const { data } = await updateStrategicImperative({
        variables: { id, updateStrategicImperative: strategicImperative },
      });

      return { data: data?.updateStrategicImperative || null, error: null };
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, strategicImperative);
        Logger.logError({
          error,
          customContext,
          transactionName: 'updateStrategicImperative.ts - useUpdateStrategicImperativeMutation',
        });

        return { data: null, error: customContext.message as string };
      }

      return { data: null, error: null };
    }
  }, [updateStrategicImperative]);

  return [executeMutation, state];
};
